import { saveAs } from "file-saver";

export interface CsvSection {
  title: string;
  headers: string[];
  rows: Array<Array<string | number>>;
}

interface SortLike {
  keytoSort: string;
  direction: "asc" | "desc";
}

// Same generic comparator every <Subject>Table.tsx already uses inline for
// its own on-screen sorting - reused here so an exported table's row order
// matches whatever the user currently has it sorted by, instead of raw API
// order.
export function sortRowsForExport<T extends Record<string, any>>(rows: T[], sort: SortLike): T[] {
  const { keytoSort, direction } = sort;
  return rows.slice().sort((a, b) => {
    const aValue = a[keytoSort];
    const bValue = b[keytoSort];
    if (typeof aValue === "string" && typeof bValue === "string") {
      return direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return direction === "asc" ? aValue - bValue : bValue - aValue;
  });
}

function escapeCsvCell(value: string | number): string {
  const stringValue = String(value);
  return /[",\n]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
}

function sectionToCsvBlock(section: CsvSection): string {
  const lines = [section.title, section.headers.map(escapeCsvCell).join(",")];
  section.rows.forEach((row) => lines.push(row.map(escapeCsvCell).join(",")));
  return lines.join("\n");
}

// One CSV file, with a blank line between each titled section - a tab with
// a single table (Performance) exports one section; a tab with several
// tables/charts (Brands' Daily+MTD tables, Users' funnel/donuts/7 step
// sections) exports one section per table/chart, all in the one file.
export function downloadCsvSections(filename: string, sections: CsvSection[]): void {
  const csvString = sections.map(sectionToCsvBlock).join("\n\n");
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, filename);
}
